import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken, deleteAuthToken } from '../util/request';
import { reset, setUser } from '../slices/userSlice';
import Auth from '../api/auth';

const AUTH_TOKEN_KEY = 'authTK';
const AUTH_USER_KEY = 'authUser';

const buildSessionUser = (authResponse) => {
  const user = authResponse?.user || {};
  return {
    id: user.id ?? null,
    email: user.email ?? '',
    superadmin: Boolean(user.superadmin),
    agency: user.agency ?? null,
    authorizePermissions: authResponse?.permissions ?? [],
  };
};

export function useAuth() {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const [isLogginIn, setIsLogginIn] = useState(false);

  const login = useCallback(
    async (credentials) => {
      setIsLogginIn(true);
      try {
        const authResponse = await Auth.login(credentials);
        const { token } = authResponse;
        const sessionUser = buildSessionUser(authResponse);
        setAuthToken(token);
        persistToken(token);
        persistUser(sessionUser);
        setIsLogginIn(false);
        dispatch(setUser(sessionUser));
      } catch (error) {
        setIsLogginIn(false);
        persistToken(null);
        throw error;
      }
    },
    [dispatch]
  );

  const logout = async () => {
    try {
      await Auth.logout();
    } finally {
      persistToken(null);
      persistUser(null);
      deleteAuthToken();
      dispatch(reset());
    }
  };

  const getPersistedToken = () => {
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
  };

  const persistToken = (token) => {
    if (token) {
      return window.localStorage.setItem(AUTH_TOKEN_KEY, token);
    }
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
  };

  const persistUser = (user) => {
    if (user) {
      return window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    }
    window.localStorage.removeItem(AUTH_USER_KEY);
  };

  return {
    isLoggedIn,
    isLogginIn,
    login,
    logout,
    getPersistedToken,
    setAuthToken,
  };
}

export default useAuth;
